import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactReason, getCsmMailtoHref } from '../../utils/response-mailto.utils';
import { DbProjectRef } from '../../models/response-project-document';

export type ResponseDialogMode =
  | 'project-deleted'
  | 'document-deleted'
  | 'quota-exceeded'
  | 'subscription-expired'
  | 'project-delete-confirm'
  | 'library-document-delete-confirm'
  | 'slot-conflict';

interface DialogConfig {
  icon: string;
  iconModifier?: 'error' | 'frozen';
  titleKey: string;
  bodyKey?: string;
  bodyParams?: Record<string, unknown>;
  showCta?: boolean;
  ctaTextKey?: string;
  reloadOnDismiss?: boolean;
  primaryDismiss?: boolean;
}

@Component({
  selector: 'app-response-dialog',
  templateUrl: './response-dialog.component.html',
  styleUrls: ['./response-dialog.component.scss'],
})
export class ResponseDialogComponent {
  @Input() mode: ResponseDialogMode = 'project-deleted';
  @Input() projectId: string | null = null;
  @Input() projectName = '';
  @Input() documentName = '';
  @Input() projects: DbProjectRef[] = [];

  @Input() isTrial = false;
  @Input() planLabel = '';

  private readonly DIALOG_CONFIGS: Record<ResponseDialogMode, DialogConfig> = {
    'project-deleted': {
      icon: 'ph-users',
      iconModifier: 'error',
      titleKey: 'tenders.responses.concurrent-modal.project-deleted-title',
      bodyKey: 'tenders.responses.concurrent-modal.project-deleted-body',
      reloadOnDismiss: true,
    },
    'document-deleted': {
      icon: 'ph-users',
      iconModifier: 'error',
      titleKey: 'tenders.responses.concurrent-modal.document-deleted-title',
      bodyKey: 'tenders.responses.concurrent-modal.document-deleted-body',
      reloadOnDismiss: true,
    },
    'quota-exceeded': {
      icon: 'ph-lock',
      titleKey: 'tenders.responses.quota-modal.title',
      bodyKey: 'tenders.responses.quota-modal.body',
      showCta: true,
      ctaTextKey: 'tenders.responses.quota-modal.cta-text',
    },
    'subscription-expired': {
      icon: 'ph-clock',
      iconModifier: 'frozen',
      titleKey: '',
      showCta: true,
    },
    'project-delete-confirm': {
      icon: 'ph-trash',
      iconModifier: 'error',
      titleKey: 'tenders.responses.delete-project-modal.title',
    },
    'library-document-delete-confirm': {
      icon: 'ph-trash',
      iconModifier: 'error',
      titleKey: 'tenders.library.delete-modal.title',
    },
    'slot-conflict': {
      icon: 'ph-warning-circle',
      iconModifier: 'error',
      titleKey: 'tenders.responses.concurrent-modal.slot-conflict-title',
      bodyKey: 'tenders.responses.concurrent-modal.slot-conflict-body',
      primaryDismiss: true,
    },
  };

  constructor(public activeModal: NgbActiveModal) {}

  get config(): DialogConfig {
    const base = this.DIALOG_CONFIGS[this.mode];
    if (this.mode === 'subscription-expired') {
      return {
        ...base,
        titleKey: this.isTrial ? 'tenders.responses.quota-modal.frozen-title-trial' : 'tenders.responses.quota-modal.frozen-title-paid',
        bodyKey: this.isTrial ? 'tenders.responses.quota-modal.frozen-body-trial' : 'tenders.responses.quota-modal.frozen-body-paid',
        ctaTextKey: this.isTrial
          ? 'tenders.responses.quota-modal.frozen-cta-text-trial'
          : 'tenders.responses.quota-modal.frozen-cta-text-paid',
      };
    }
    return base;
  }

  get titleParams(): Record<string, unknown> | undefined {
    if (this.mode === 'subscription-expired' && this.isTrial) return { plan: this.planLabel };
    return undefined;
  }

  get isConfirmMode(): boolean {
    return this.mode === 'project-delete-confirm' || this.mode === 'library-document-delete-confirm';
  }

  get isLibraryDeleteConfirm(): boolean {
    return this.mode === 'library-document-delete-confirm';
  }

  reload(): void {
    if (this.mode === 'document-deleted' && this.projectId) {
      window.location.href = `/tenders/responses/${this.projectId}`;
    } else {
      window.location.href = '/tenders/responses';
    }
  }

  confirm(): void {
    this.activeModal.close(true);
  }

  get mailtoHref(): string {
    if (this.mode === 'quota-exceeded') return getCsmMailtoHref(ContactReason.CreditsOut);
    return getCsmMailtoHref(this.isTrial ? ContactReason.TrialEnded : ContactReason.Expired);
  }
}
